import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useApi, useWorkspace } from '~/contexts';
import { useFeatures, useLocalStorage } from '~/hooks';

const AssistantContext = createContext();

const MODES = { panel: 'panel', drawer: 'drawer' };
const PANEL_MIN_WIDTH = 380;
const defaultPanelWidth = () => Math.max(PANEL_MIN_WIDTH, Math.floor(window.innerWidth * 0.25));
const DRAWER_WIDTH = 1032;
const SIDEBAR_DEFAULTS = { panel: false, drawer: true };

// Animation timing — toggle TRANSITION_DEBUG for slow-mo testing
const TRANSITION_DEBUG = false;
const TRANSITION_MS = TRANSITION_DEBUG ? 1500 : 250;
const TRANSITION = `${TRANSITION_MS}ms ease`;

export { defaultPanelWidth, PANEL_MIN_WIDTH, DRAWER_WIDTH, TRANSITION_MS, TRANSITION };

export function AssistantProvider({ children }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const isBillingLocked = !workspace.billingStatus?.isValid;
  const isDisabled = workspace.statusId === 'disabled';
  const assistantEnabled = workspace.member.assistantEnabled;

  const openStorage = useLocalStorage({ key: 'assistant_panel_open' });
  const modeStorage = useLocalStorage({ key: 'assistant_panel_mode' });

  // Profile state
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState(null);

  const refreshProfiles = useCallback(async () => {
    if (!features.assistant || isBillingLocked || isDisabled || !assistantEnabled) return;
    const { data } = await api.www.workspaces(workspace.id).ai().assistantProfiles().list();
    setProfiles(data);
    setProfileId((prev) => {
      if (prev && data.some((p) => p.id === prev)) return prev;
      const defaultProfile = data.find((p) => p.scopeKey === 'system') || data[0];
      return defaultProfile?.id ?? null;
    });
  }, [api, workspace.id, features.assistant, isBillingLocked, isDisabled, assistantEnabled]);

  useEffect(() => {
    refreshProfiles();
  }, [refreshProfiles]);

  const [isOpen, setIsOpen] = useState(() => openStorage.get() === 'true');
  const [mode, setModeState] = useState(() => {
    const stored = modeStorage.get();
    // Migrate old modes to current ones
    if (stored === 'narrow' || stored === 'wide') return MODES.panel;
    if (stored === 'full') return MODES.drawer;
    return Object.values(MODES).includes(stored) ? stored : MODES.panel;
  });

  useEffect(() => {
    openStorage.set(String(isOpen));
  }, [isOpen, openStorage]);

  useEffect(() => {
    modeStorage.set(mode);
  }, [mode, modeStorage]);

  // Force-close the panel when the feature is unavailable (handles stale localStorage)
  useEffect(() => {
    if (!features.assistant || isBillingLocked || isDisabled || !assistantEnabled) {
      setIsOpen(false);
    }
  }, [features.assistant, isBillingLocked, isDisabled, assistantEnabled]);

  const openPanel = useCallback(() => {
    if (!features.assistant || isBillingLocked || isDisabled || !assistantEnabled) return;
    setIsOpen(true);
  }, [features.assistant, isBillingLocked, isDisabled, assistantEnabled]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePanel = useCallback(() => {
    if (isOpen) {
      closePanel();
    } else {
      openPanel();
    }
  }, [isOpen, openPanel, closePanel]);

  // Sidebar state
  const sidebarStorage = useLocalStorage({ key: 'assistant_sidebar_open' });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const stored = sidebarStorage.get();
    if (stored !== null) return stored === 'true';
    return SIDEBAR_DEFAULTS[mode] ?? false;
  });

  useEffect(() => {
    sidebarStorage.set(String(sidebarOpen));
  }, [sidebarOpen, sidebarStorage]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Drag state (shared so layout can disable transitions)
  const [isDragging, setIsDragging] = useState(false);

  // Drag-to-resize (persisted, only for panel mode)
  const widthStorage = useLocalStorage({ key: 'assistant_panel_width' });
  const [panelWidthOverride, setPanelWidthOverrideRaw] = useState(() => {
    const stored = widthStorage.get();
    return stored ? Number(stored) : null;
  });

  const setPanelWidthOverride = useCallback(
    (value) => {
      setPanelWidthOverrideRaw(value);
      widthStorage.set(value != null ? String(value) : '');
    },
    [widthStorage],
  );

  // Remember drawer sidebar state so it persists across mode switches and refreshes
  const drawerSidebarStorage = useLocalStorage({ key: 'assistant_drawer_sidebar_open' });

  const setMode = useCallback(
    (newMode) => {
      if (!Object.values(MODES).includes(newMode)) return;
      if (newMode === MODES.panel) {
        // Save current drawer sidebar state, then close for panel mode
        drawerSidebarStorage.set(String(sidebarOpen));
        setSidebarOpen(false);
      } else {
        // Restore drawer sidebar state
        const stored = drawerSidebarStorage.get();
        const drawerState = stored !== null ? stored === 'true' : true;
        setSidebarOpen(drawerState);
      }
      setModeState(newMode);
      if (!isOpen) {
        setIsOpen(true);
      }
    },
    [isOpen, sidebarOpen, drawerSidebarStorage],
  );

  // Force a specific layout offset (used during drawer→panel collapse)
  const [forcePanelWidth, setForcePanelWidth] = useState(null);

  // Width used for layout offset (pushes main content)
  const panelWidth = useMemo(() => {
    if (forcePanelWidth != null) return forcePanelWidth;
    if (!isOpen) return 0;
    if (mode === 'panel') return panelWidthOverride || defaultPanelWidth();
    return 0; // drawer doesn't push content
  }, [isOpen, mode, panelWidthOverride, forcePanelWidth]);

  // Actual rendered width of the panel element
  const renderedPanelWidth = useMemo(() => {
    if (!isOpen) return 0;
    if (mode === 'panel') return panelWidthOverride || defaultPanelWidth();
    if (mode === 'drawer') return DRAWER_WIDTH;
    return 0;
  }, [isOpen, mode, panelWidthOverride]);

  const value = useMemo(
    () => ({
      isOpen,
      mode,
      openPanel,
      closePanel,
      togglePanel,
      setMode,
      panelWidth,
      renderedPanelWidth,
      panelWidthOverride,
      setPanelWidthOverride,
      setForcePanelWidth,
      isDragging,
      setIsDragging,
      sidebarOpen,
      setSidebarOpen,
      toggleSidebar,
      profiles,
      profileId,
      setProfileId,
      refreshProfiles,
    }),
    [
      isOpen,
      mode,
      openPanel,
      closePanel,
      togglePanel,
      setMode,
      panelWidth,
      renderedPanelWidth,
      panelWidthOverride,
      setPanelWidthOverride,
      setForcePanelWidth,
      isDragging,
      sidebarOpen,
      toggleSidebar,
      profiles,
      profileId,
      refreshProfiles,
    ],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant() {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within AssistantProvider');
  return ctx;
}
